import { Avatar, AvatarProps, SxProps, Theme } from '@mui/material';
import { useEffect, useState } from 'react';

/** Iniciales a partir de nombre (primera + última palabra) o del email. */
export function userInitials(name?: string | null, email?: string | null): string {
  const n = (name || '').trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      const a = parts[0][0] || '';
      const b = parts[parts.length - 1][0] || '';
      return (a + b).toUpperCase();
    }
    return n.slice(0, 2).toUpperCase();
  }
  const e = (email || '').trim();
  if (e) {
    const local = e.split('@')[0] || e;
    const letters = local.replace(/[^a-zA-Z0-9]/g, '');
    if (letters.length >= 2) return letters.slice(0, 2).toUpperCase();
    if (letters.length === 1) return (letters + (e[1] || letters)).slice(0, 2).toUpperCase();
    return e.slice(0, 2).toUpperCase();
  }
  return '?';
}

function hueFromString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = s.charCodeAt(i) + ((h << 5) - h);
  }
  return Math.abs(h) % 360;
}

export type UserAvatarProps = Omit<AvatarProps, 'src' | 'alt'> & {
  src?: string | null;
  name?: string | null;
  email?: string | null;
  alt?: string;
};

/**
 * Avatar de usuario con foto o, si no hay / falla la imagen, iniciales sobre un gradiente suave.
 */
export default function UserAvatar({
  src,
  name,
  email,
  alt,
  children,
  sx,
  ...rest
}: UserAvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const label = (name || '').trim() || (email || '').trim() || '';
  const initials = userInitials(name, email);
  const hue = hueFromString(label || initials);
  const trimmedSrc = src && String(src).trim() !== '' ? src : undefined;
  const showImg = Boolean(trimmedSrc) && !imgFailed;

  useEffect(() => {
    setImgFailed(false);
  }, [trimmedSrc]);

  const displayAlt = alt || label || 'User';

  const fallbackSx: SxProps<Theme> = !showImg
    ? {
        background: `linear-gradient(135deg, hsl(${hue}, 62%, 52%) 0%, hsl(${(hue + 42) % 360}, 58%, 42%) 100%)`,
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.8125rem',
        letterSpacing: '-0.04em',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
      }
    : {};

  return (
    <Avatar
      alt={displayAlt}
      src={showImg ? trimmedSrc : undefined}
      imgProps={{
        onError: () => setImgFailed(true),
      }}
      sx={[fallbackSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...rest}
    >
      {!showImg ? children ?? initials : null}
    </Avatar>
  );
}
